
import React, { useState, useEffect } from 'react';
import API_URL from '../../../config';
// import DashboardLayout from '../../layouts/DashboardLayout'; // Removed wrapper
import { AdminService } from '../../../application/services/AdminService';
import Modal from '../../components/ui/Modal';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { 
    Search, 
    Plus, 
    Loader2,
    Edit2,
    Trash2
} from 'lucide-react';
import Button from '../../components/ui/Button';
import UsuarioForm from './UsuarioForm';


const UserAvatar = ({ user }) => {
    const [imgError, setImgError] = useState(false);

    if (user.foto && !imgError) {
        return (
            <img 
                className="h-10 w-10 rounded-full object-cover" 
                src={`${API_URL}${user.foto}`}
                alt={user.nombre}
                onError={() => setImgError(true)}
            />
        );
    }

    return (
        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
            {user.nombre?.charAt(0).toUpperCase()}
        </div>
    );
};

const ROLE_NAMES = {
    'A': 'Administrador',
    'S': 'Secretario',
    'T': 'Tesorero',
    'L': 'Lecturador',
    'R': 'Super Admin',
    'O': 'Operador'
};

const UsuariosList = () => {
    const { isSuperAdmin, user } = useAuth();
    const [usuarios, setUsuarios] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    
    // Action Modal State (Toggle Status)
    const [actionUser, setActionUser] = useState(null);

    const toast = useToast();

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const promises = [AdminService.getAllUsuarios()];
            
            // Solo cargar empresas si es super admin para el filtro/creación
            if (isSuperAdmin()) {
                promises.push(AdminService.getEmpresas());
            }

            const [usersData, empresasData] = await Promise.all(promises);
            
            setUsuarios(usersData);
            if (isSuperAdmin()) {
                setEmpresas(empresasData);
            }
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setSelectedUser(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleSuccess = () => {
        fetchData();
        setIsModalOpen(false);
    };

    const handleStatusToggle = (user) => {
        setActionUser(user);
    };

    const confirmToggleStatus = async () => {
        if (!actionUser) return;

        try {
            const newStatus = !actionUser.activo;
            await AdminService.updateUsuario(actionUser.id, { activo: newStatus });
            toast.success(`Usuario ${newStatus ? 'activado' : 'desactivado'} exitosamente`);
            fetchData();
        } catch (error) {
            console.error(error);
            toast.error('Error al actualizar estado del usuario');
        } finally {
            setActionUser(null);
        }
    };

    const filteredUsers = usuarios.filter(u => 
        u.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.usuario?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.nombreEmpresa?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Gestión de Usuarios</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Administra los usuarios {isSuperAdmin() ? 'de todas las Juntas de Agua registradas' : 'de tu Junta de Agua'}.
                    </p>
                </div>
                <Button onClick={handleOpenCreate} className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700">
                    <Plus className="w-4 h-4" />
                    Nuevo Usuario
                </Button>
            </div>

            {/* Modal Form */}
            {isModalOpen && (
                <UsuarioForm 
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={handleSuccess}
                    initialData={selectedUser}
                    empresas={empresas}
                    isSuperAdmin={isSuperAdmin()} 
                    currentUserEmpresaId={user?.id_empresa}
                />
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex items-center">
                <Search className="w-5 h-5 text-gray-400 mr-3" />
                <input 
                    type="text" 
                    placeholder="Buscar por nombre, usuario, email..."
                    className="flex-1 outline-none text-gray-600 placeholder-gray-400"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold">
                                <th className="px-3 py-2">Usuario</th>
                                <th className="px-3 py-2">Cédula</th>
                                {isSuperAdmin() && <th className="px-3 py-2">Empresa</th>}
                                <th className="px-3 py-2">Rol</th>
                                <th className="px-3 py-2">Estado</th>
                                <th className="px-3 py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        <div className="flex justify-center items-center">
                                            <Loader2 className="w-6 h-6 animate-spin mr-2" />
                                            Cargando usuarios...
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                        No se encontraron usuarios
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-3 py-2">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <UserAvatar user={user} />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{user.nombre}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-600">{user.cedula || 'Sin cédula'}</td>
                                        {isSuperAdmin() && (
                                            <td className="px-3 py-2 text-sm text-gray-600">
                                                {user.Empresa?.nombre || user.empresa?.nombre || user.nombreEmpresa || '-'}
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-sm text-gray-600">
                                            {(() => {
                                                const rawRole = user.roles && user.roles[0] ? user.roles[0] : 'Usuario';
                                                return ROLE_NAMES[rawRole] || rawRole;
                                            })()}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                user.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {user.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleOpenEdit(user)}
                                                    className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                                    title="Editar usuario"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    onClick={() => handleStatusToggle(user)}
                                                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                                    title={user.activo ? 'Desactivar usuario' : 'Activar usuario'}
                                                >
                                                    <Trash2 className="w-4 h-4" /> 
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Confirmation Modal */}
            <Modal
                isOpen={!!actionUser}
                onClose={() => setActionUser(null)}
                title={actionUser?.activo ? 'Desactivar Usuario' : 'Activar Usuario'}
                size="sm"
                footer={
                    <>
                        <Button 
                            variant="secondary" 
                            onClick={() => setActionUser(null)}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            onClick={confirmToggleStatus}
                            className={actionUser?.activo ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
                        >
                            {actionUser?.activo ? 'Confirmar Desactivación' : 'Confirmar Activación'}
                        </Button>
                    </>
                }
            >
                <div>
                    <p className="text-gray-600">
                        ¿Estás seguro de que deseas {actionUser?.activo ? 'desactivar' : 'activar'} al usuario 
                        <span className="font-bold"> {actionUser?.nombre}</span>?
                    </p>
                    {actionUser?.activo && (
                        <p className="mt-2 text-sm text-red-500">
                            El usuario perderá acceso al sistema.
                        </p>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default UsuariosList;
